import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import Store from 'lightningjs-redux';
import { getLayoutTheme } from '../tornado/utils/theme';
import { LETTER_SELECTED_SIGNAL } from '../tornado/keyboard/keyboard.constants';
import { SELECTED_SIGNAL } from '../tornado/signals';
import { getSearchedItems, searchAll, searchKeyInput, stopSearch } from '../../logic/search/search.actions';
import Keyboard from '../tornado/keyboard/keyboard.component';
import SearchField from './searchField/searchField.component';
import SearchResultBlock from './blocks/blocks.component';
import SearchHistory from './searchHistory/searchHistory.component';
import { defaultContext, getInitialStyle, BACKGROUND_COLOR } from './search.style';
import connect from './search.connect';

export class Search extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        SearchField: {
          type: SearchField,
          searchQuery: '',
        },
        Keyboard: {
          type: Keyboard,
          signals: {
            [LETTER_SELECTED_SIGNAL]: '_handleKeyPress',
          },
        },
        SearchHistory: {
          type: SearchHistory,
          signals: {
            [SELECTED_SIGNAL]: '_handleSearchFromHistory',
          },
        },
        Strip: {
          rect: true,
        },
        Overlay: {
          rect: true,
        },
        SearchResultBlock: {
          type: SearchResultBlock,
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Keyboard extends this {
        $enter() {
          if (!this._focused) {
            this._focused = this.tag('Keyboard');
          }
        }

        _getFocused() {
          return this._focused;
        }

        _handleDown() {
          if (this._focused === this.tag('Keyboard') && this.tag('SearchHistory').isVisibleHistory) {
            this._focused = this.tag('SearchHistory');

            return true;
          }

          return false;
        }

        _handleUp() {
          if (this._focused === this.tag('SearchHistory')) {
            this._focused = this.tag('Keyboard');
          }

          return false;
        }

        _handleRight() {
          this._setState('Content');

          return true;
        }
      },
      class Content extends this {
        _getFocused() {
          return this.tag('SearchResultBlock');
        }

        _handleLeft() {
          this._setState('Keyboard');

          return true;
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
    this._query = '';
  }

  _attach() {
    this._unsubscribe = connect(this);
    this._fallbackRequestAllowed = true;
    this._setState('Keyboard');
    Store.dispatch(getSearchedItems());
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    this._data = null;
    this._query = '';

    Store.dispatch(stopSearch());
  }

  /**
   * Lightning Router history state handler.
   * Called without arguments on initial load, and with a history object on backward navigation.
   *
   * @param {Object} history
   * @returns {Object} Current state for history storage
   */
  historyState(history) {
    const renderer = this.tag('SearchResultBlock');

    // Restoring on backward navigation
    if (history) {
      if (Number.isFinite(history.column)) {
        this._setState('Content', [true]);
      }
      this._query = history.query?.query || '';
      this.tag('SearchField').searchQuery = this._query;
    }

    // Returning current state for history storage
    const result = renderer.historyState(history);
    if (result && this._query) {
      result.query = { query: this._query };
    }

    return result;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    // eslint-disable-next-line no-console
    console.log(`[Search] Layout query: ${value?.queryParameters?.query}`);

    if (!value) {
      return;
    }

    this._data = value;
    const theme = getLayoutTheme(_get(value, 'layout.theme'));
    const color = theme.backgroundColor || BACKGROUND_COLOR[this._style.colorMode];

    this.patch({
      Background: { color },
      Strip: { color },
      Overlay: { colorTop: color },
    });

    this.tag('SearchResultBlock').data = this._data;
    this._refocus();
  }

  get query() {
    return this._query;
  }

  set query(value) {
    if (this._query === value) {
      return;
    }

    this._query = value;
    this.tag('SearchField').searchQuery = this._query;
  }

  set searchHistory(value) {
    this.tag('SearchHistory').searchedItems = value;
  }

  _handleKeyPress(symbol) {
    Store.dispatch(searchKeyInput(symbol));
  }

  _handleSearchFromHistory(item) {
    this.query = item;
    // NOTE: we are omitting the debounced search (i.e. appending the query char by char) here to avoid delay
    Store.dispatch(stopSearch());
    Store.dispatch(searchAll(this._query));
  }
}
